import React, { useMemo } from 'react';
import { BsDownload, BsEnvelope, BsTrash } from 'react-icons/bs';
import { FaPlus } from 'react-icons/fa';
import { FaPencil } from 'react-icons/fa6';
import { useDispatch, useSelector } from 'react-redux';
import {
  Box, Button, Flex, HStack,
  Icon, IconButton, Switch,
  Text, Tooltip, VStack
} from '@chakra-ui/react';

import NoResultsMessage from '../../../../components/NoResultsMessage';
import TooltipIcon from '../../../../components/TooltipIcon';
import nothingHere from '../../../../nothing-here.png';
import {
  deleteAutomationStep,
  selectCurrentAutomation,
  updateAutomation
} from '../../../../redux/modules/emailAutomations';
import { emailEditorReset } from '../../../../redux/modules/emails';
import { hideModal, showConfirmModal, showModal } from '../../../../redux/modules/modal';
import colors from '../../../../styles/theme/foundation/colors';
import { getDateString } from '../../../../utils/dateUtil';

import AutomationTabHeader from './AutomationTabHeader';
import CreateStepForm from './CreateStepForm';
import PreviewStepModal from './PreviewStepModal';
import ReportDownloadModal from './ReportDownloadModal';

const AutomationSettings = () => {
  const dispatch = useDispatch();
  const currentAutomation = useSelector(selectCurrentAutomation);

  const enabledText = useMemo(() => {
    if (currentAutomation?.isEnabled) {
      return 'You can disable this automation at any time to prevent email sequences from executing.';
    }

    return 'Once your sequence is ready, you can enable this automation to begin sending emails automatically!';
  }, [currentAutomation]);

  const handleCreateStepClick = () => {
    dispatch(showModal(CreateStepForm, {
      size: '2xl',
      onClose: () => dispatch(emailEditorReset())
    }));
  };

  const handleUpdateStepClick = stepId => () => dispatch(showModal(CreateStepForm, {
    size: '2xl',
    onClose: () => dispatch(emailEditorReset())
  }, { stepId }));

  const handleDeleteStepClick = step => () => dispatch(showConfirmModal(
    `Delete ${step?.name}`,
    () => {
      dispatch(deleteAutomationStep(step.id));
      dispatch(hideModal());
    }
  ));

  const handlePreviewStepClick = stepId => () => {
    dispatch(showModal(PreviewStepModal, { size: 'xl' }, { stepId }));
  };

  const handleDownloadReportClick = step => () => {
    dispatch(showModal(ReportDownloadModal, { size: 'xl' }, { step }));
  };

  if (!currentAutomation) {
    return (
      <VStack alignItems="flex-start" flex={1} maxW="600px" ml="100px" p={4} spacing={8}>
        <AutomationTabHeader />
        <NoResultsMessage
          altTag="Create an Automation to Begin"
          image={nothingHere}
          message="Create an Automation to Begin"
        />
      </VStack>
    );
  }

  return (
    <VStack alignItems="flex-start" flex={1} maxW="800px" ml="100px" p={4} spacing={8}>
      <AutomationTabHeader />

      <HStack justifyContent="space-between" w="100%">
        <Box pr={4} textAlign="left">
          <Text mb={4} textStyle="bodyMd500">Automation Enabled</Text>
          <Text color="greyScale.500" textStyle="bodySm">
            {enabledText}
          </Text>
        </Box>
        <Box>
          <Switch
            isChecked={currentAutomation?.isEnabled}
            onChange={e => {
              dispatch(updateAutomation(currentAutomation.id, { is_enabled: e.target.checked }));
            }}
          />
        </Box>
      </HStack>

      <Text
        color="greyScale.500"
        maxW="400px"
        textAlign="left"
        textStyle="displayXxs"
      >
        Automation Steps
      </Text>

      <VStack alignItems="center" m="0 auto" w="100%">
        {currentAutomation?.steps && currentAutomation?.steps?.map((step, index) => {
          const updatedAt = step.createdAt === step.updatedAt ? `step created ${getDateString(step.createdAt)}` : `step updated ${getDateString(step.updatedAt)}`;
          return (
            <>
              {index > 0 && (
                <Flex alignItems="stretch" h="20px">
                  <Box borderRight={`3px solid ${colors.primaryScale['200']}`} />
                  <Box />
                </Flex>
              )}
              <Flex
                alignItems="center"
                bg="primaryScale.50"
                borderRadius="md"
                boxShadow="soft1"
                justifyContent="space-between"
                p={4}
                w="100%"
              >
                <Box>
                  <Tooltip label={updatedAt} openDelay={200} placement="top">
                    <Text fontWeight="bold" maxW="300px" textAlign="left" textStyle="body">
                      {step.name}
                    </Text>
                  </Tooltip>
                  {/* <Text color={colors.greyScale['600']} maxW="300px" textAlign="left" textStyle="bodyXs"> */}
                  {/*  {updatedAt} */}
                  {/* </Text> */}
                </Box>
                <HStack mr={2} spacing={8}>
                  <TooltipIcon
                    as={FaPencil}
                    color="primaryScale.600"
                    cursor="pointer"
                    label="Edit Step"
                    width={4}
                    onClick={handleUpdateStepClick(step.id)}
                  />
                  <TooltipIcon
                    as={BsTrash}
                    color="error.primary.600"
                    cursor="pointer"
                    label="Delete Step"
                    width={4}
                    onClick={handleDeleteStepClick(step)}
                  />
                  <TooltipIcon
                    as={BsEnvelope}
                    color="primaryScale.400"
                    cursor="pointer"
                    label="Preview Email"
                    width={4}
                    onClick={handlePreviewStepClick(step.id)}
                  />
                  <TooltipIcon
                    as={BsDownload}
                    color="primaryScale.400"
                    cursor="pointer"
                    label="Download Report"
                    width={4}
                    onClick={handleDownloadReportClick(step)}
                  />
                </HStack>
              </Flex>
            </>
          );
        })}

        {currentAutomation.steps.length && (
          <>
            <Flex alignItems="stretch" h="30px">
              <Box borderRight={`3px solid ${colors.primaryScale['200']}`} />
              <Box />
            </Flex>
            <IconButton aria-label="Add Step" icon={<Icon as={FaPlus} width={10} />} onClick={handleCreateStepClick} />
          </>
        )}

        {currentAutomation.steps.length === 0 && (
          <Button alignSelf="flex-start" onClick={handleCreateStepClick}>
            Add First Step
          </Button>
        )}
      </VStack>
    </VStack>
  );
};

export default AutomationSettings;
